import { useEffect, useState, useSyncExternalStore, type ReactNode } from 'react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useAuth } from '@/hooks/use-auth';
import { UploadBatchContext } from '@/hooks/upload-batch-context';
import { registerSessionChangeGuard, type SessionChange } from '@/lib/session-change-guard';
import { UploadBatchStore } from '@/lib/upload-batch-store';

interface PendingChange {
  readonly change: SessionChange;
  readonly decide: (proceed: boolean) => void;
}

const CHANGE_COPY: Readonly<Record<SessionChange, { readonly title: string; readonly action: string }>> = {
  'switch-workspace': { title: 'Switch workspace and cancel uploads?', action: 'Cancel uploads and switch' },
  'sign-out': { title: 'Sign out and cancel uploads?', action: 'Cancel uploads and sign out' },
};

/**
 * Uploads are cancelled while the API session still belongs to the workspace
 * that owns them; after the change the store is replaced without touching the
 * server.
 */
function useSessionChangeGuard(store: UploadBatchStore) {
  const [pending, setPending] = useState<PendingChange | null>(null);
  useEffect(() => registerSessionChangeGuard(async change => {
    if (!store.getActivity().sending) return true;
    const proceed = await new Promise<boolean>(decide => { setPending({ change, decide }); });
    setPending(null);
    if (proceed) await store.cancelActive();
    return proceed;
  }), [store]);
  return pending;
}

function OwnedUploads({ children }: { readonly children: ReactNode }) {
  const [store] = useState(() => new UploadBatchStore());
  const { sending } = useSyncExternalStore(store.subscribe, store.getActivity, store.getActivity);
  const pending = useSessionChangeGuard(store);
  useEffect(() => store.retain(), [store]);
  useEffect(() => {
    if (!sending) return;
    const preventClose = (event: BeforeUnloadEvent) => { event.preventDefault(); };
    window.addEventListener('beforeunload', preventClose);
    return () => { window.removeEventListener('beforeunload', preventClose); };
  }, [sending]);
  const copy = pending === null ? null : CHANGE_COPY[pending.change];
  return <UploadBatchContext value={store}>
    {children}
    <AlertDialog open={pending !== null} onOpenChange={open => { if (!open) pending?.decide(false); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{copy?.title}</AlertDialogTitle>
          <AlertDialogDescription>
            Files are still being sent from this tab. Leaving stops them and releases what was reserved. Files the server already received keep processing.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Keep uploading</AlertDialogCancel>
          <AlertDialogAction onClick={() => { pending?.decide(true); }}>{copy?.action}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </UploadBatchContext>;
}

/** Transfers belong to one signed-in workspace member; a new member gets a new store. */
export function UploadBatchProvider({ children }: { readonly children: ReactNode }) {
  const { user } = useAuth();
  const viewer = user === null ? 'anonymous' : `${user.user_id}:${user.tenant_id ?? ''}:${user.membership_id ?? ''}`;
  return <OwnedUploads key={viewer}>{children}</OwnedUploads>;
}
